import {
	expenseJpy,
	settleExpenses,
	tripParty,
	type TripWorkspace,
} from "@japangolmok/core";

export type JourneySummaryData = {
	people: { id: string; name: string }[];
	days: { id: string; label: string; places: string[] }[];
	extraDays: number;
	sharedJpy: number;
	totalJpy: number;
	excluded: number;
	transfer: {
		from: string;
		to: string;
		amount: number;
		additional: number;
	} | null;
};

export const sampleSummary: JourneySummaryData = {
	people: [
		{ id: "minji", name: "민지" },
		{ id: "junho", name: "준호" },
		{ id: "seoyeon", name: "서연" },
	],
	days: [
		{
			id: "1",
			label: "1일차",
			places: ["오호리공원", "후쿠오카시미술관", "하카타 저녁"],
		},
		{
			id: "2",
			label: "2일차",
			places: ["다자이후 텐만구", "캐널시티", "나카스"],
		},
		{ id: "3", label: "3일차", places: ["모모치해변", "후쿠오카타워", "공항"] },
	],
	extraDays: 0,
	sharedJpy: 70860,
	totalJpy: 77360,
	excluded: 0,
	transfer: { from: "민지", to: "준호", amount: 16660, additional: 1 },
};

export function JourneySummary({
	summary,
	budgetHref,
}: {
	summary: JourneySummaryData;
	budgetHref?: string;
}) {
	return (
		<div className="journey-summary">
			<ul
				className="journey-party"
				aria-label={`일행 ${summary.people.length}명`}
			>
				{summary.people.map((person) => (
					<li key={person.id}>{person.name}</li>
				))}
			</ul>
			<ol className="journey-days" aria-label="날짜별 일정">
				{summary.days.map((day) => (
					<li key={day.id}>
						<strong>{day.label}</strong>
						<span>
							{day.places.length
								? day.places.join(" · ")
								: "아직 담은 장소가 없어요"}
						</span>
					</li>
				))}
			</ol>
			{summary.extraDays > 0 && (
				<p>그 밖에 {summary.extraDays}일이 더 있어요.</p>
			)}
			<div className="journey-money">
				<span>
					함께 쓴 돈 <strong>¥{summary.sharedJpy.toLocaleString()}</strong>
				</span>
				{summary.totalJpy !== summary.sharedJpy && <span>
					전체 지출 <strong>¥{summary.totalJpy.toLocaleString()}</strong>
				</span>}
				{summary.transfer ? (
					<span>
						{summary.transfer.from} → {summary.transfer.to}{" "}
						<strong>¥{summary.transfer.amount.toLocaleString()}</strong>
						{summary.transfer.additional
							? ` 외 ${summary.transfer.additional}건`
							: ""}
					</span>
				) : (
					<span>
						{summary.excluded
							? "환율 없는 지출은 별도"
							: "정산할 금액이 없어요"}
					</span>
				)}
				{summary.excluded > 0 && (
					<small>
						환율 없는 지출 {summary.excluded}건은 합계와 정산에서 빠져 있어요.
					</small>
				)}
				{budgetHref && <a href={budgetHref}>지출과 정산 자세히 보기 →</a>}
			</div>
		</div>
	);
}

export function journeySummaryFromTrip(
	trip: TripWorkspace,
): JourneySummaryData {
	const party = tripParty(trip);
	const settlement = settleExpenses(trip);
	const transfer = settlement.rows[0];
	const name = (id: string) =>
		party.find((person) => person.id === id)?.nickname ?? "일행";
	return {
		people: party.map((person) => ({ id: person.id, name: person.nickname })),
		days: trip.days.slice(0, 3).map((day, index) => ({
			id: day.date,
			label: `${index + 1}일차`,
			places: day.items
				.filter((item) => !item.deferred)
				.slice(0, 3)
				.map((item) => item.title),
		})),
		extraDays: Math.max(0, trip.days.length - 3),
		sharedJpy: trip.expenses
			.filter((item) => (item.splitWith?.length ?? party.length) > 1)
			.reduce(
				(sum, item) => sum + (expenseJpy(item, trip.krwPer100Jpy) ?? 0),
				0,
			),
		totalJpy: trip.expenses.reduce(
			(sum, item) => sum + (expenseJpy(item, trip.krwPer100Jpy) ?? 0),
			0,
		),
		excluded: settlement.excluded,
		transfer: transfer
			? {
					from: name(transfer.from),
					to: name(transfer.to),
					amount: transfer.amountJpy,
					additional: Math.max(0, settlement.rows.length - 1),
				}
			: null,
	};
}
